import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { IconPackage } from "@tabler/icons-react";
import type { ProductDto } from "../../../../api";
import { Text } from "../../../../components/common/typography";
import { EmptyState, type EmptyStateProps } from "../../../../components/common/empty-state";
import { Pagination } from "../../../../components/common/pagination";
import { AppRoutes } from "../../../../common/routes/app_routes";
import type { PaginatedResponse } from "../../../pagination/data";
import { usePaginationSortingUrlSync } from "../../../pagination/hooks/usePaginationSortingUrlSync";
import { ProductListRow } from "./product-list-row";
import type { ProductListRowProps } from "./product-list-row";
import { getProductDetailsUrl } from "../../utils/get-product-details-url";
interface ProductsListViewProps<TQuery> {
    useDataHook: (query?: TQuery) => {
        data?: PaginatedResponse<ProductDto>;
        isLoading: boolean;
        error?: Error;
    };
    baseQuery?: TQuery;
    pageSize?: number;
    emptyState?: EmptyStateProps;
    productDetailsRoute?: string;
    cardPropsGetter?: (product: ProductDto) => Partial<ProductListRowProps>;
    compareMode?: boolean;
    selectedProducts?: Set<number>;
    onProductToggle?: (product: ProductDto) => void;
    /** When true, disables unselected Select pills — used when the compare list is full. */
    compareDisabled?: boolean;
}
export function ProductsListView<TQuery>({ useDataHook, baseQuery = {} as TQuery, pageSize = 10, emptyState, productDetailsRoute = AppRoutes.PRODUCT_DETAILS, cardPropsGetter, compareMode = false, selectedProducts, onProductToggle, compareDisabled = false, }: ProductsListViewProps<TQuery>) {
    const navigate = useNavigate();
    const { pagination, setPagination, sortingQuery } = usePaginationSortingUrlSync({ defaultPageSize: pageSize });
    const hookQuery: TQuery = {
        ...baseQuery,
        ...sortingQuery,
        page: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
    } as TQuery;
    const swr = useDataHook(hookQuery);
    const response = swr.data;
    const isLoading = swr.isLoading ?? (!response && !swr.error);
    const error = swr.error;
  const isEmpty = !isLoading && (!response?.data || response.data.length === 0);
  const openProductDetails = (product: ProductDto) => {
    navigate(
      getProductDetailsUrl(
        product.id,
        product.variantId ?? null,
        productDetailsRoute,
      ),
    );
  };
    const defaultEmptyState: EmptyStateProps = useMemo(() => ({
        icon: IconPackage,
        title: "No products yet",
    }), []);
    if (error) {
        return (<div className="flex h-full min-h-[400px] items-center justify-center">
        <Text variant="body" className="text-accentError">
          Error loading data: {error.message}
        </Text>
      </div>);
    }
    if (isEmpty) {
        return (<div className="flex h-full min-h-[400px] items-center justify-center">
        <EmptyState {...(emptyState || defaultEmptyState)}/>
      </div>);
    }
    return (<div className="flex h-full flex-col">
      {isLoading ? (<div className="flex flex-col gap-4">
          {Array.from({ length: pageSize }).map((_, index) => (<div key={index} className="h-[170px] animate-pulse rounded-2xl bg-bgLight"/>))}
        </div>) : (<>
          <div className="flex flex-col gap-4 pb-6">
            {response?.data?.map((product) => {
                const isSelected = selectedProducts?.has(product.id) ?? false;
                return (<ProductListRow key={product.id} product={product} onClick={() => compareMode
                        ? onProductToggle?.(product)
                        : openProductDetails(product)} onViewDetails={() => openProductDetails(product)} selectable={compareMode} selected={isSelected} compareDisabled={compareDisabled} onToggleSelect={() => onProductToggle?.(product)} {...(cardPropsGetter ? cardPropsGetter(product) : undefined)}/>);
            })}
          </div>

          {response?.meta &&
                response.meta.pageCount &&
                response.meta.pageCount > 1 && (<div className="mt-auto flex justify-center border-t border-borderDefault pt-4">
                <Pagination page={pagination.pageIndex + 1} pageCount={response.meta.pageCount} onPageChange={(page) => setPagination({ ...pagination, pageIndex: page - 1 })}/>
              </div>)}
        </>)}
    </div>);
}
export default ProductsListView;
